/* This component use all the element of custom ReusableComponents/Table component
 * to create a full table with pagination and sorting.
 */

import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Grid, TableCell, TableRow } from '@mui/material';
import HeaderRow from '../HeaderRow';
import HeaderCell from '../HeaderCell';
import LinearProgress from '../../LinearProgress';
import Pagination from '../../Pagination';
import Table from '../Table';
import TableBody from '../TableBody';
import TableHead from '../TableHead';
import TablePaper from '../TablePaper';
import styles from './TableFull.module.css';
import SortIcon from '../SortIcon';

/**
 * Creates the table headers from objects imported here.
 *
 * @param {array<{
 *   value: string,
 *   label: string | React.Element,
 *   isSortable: boolean,
 * }>} headerCells array of objects with label and value
 * @param {function} onSortUpdate function to update sort order
 * @param {object} sortParams object with sort order and sort by values
 *
 * @returns {object} header cell components to be used by the table.
 */
const createHeader = (headerCells, onSortUpdate, sortParams) => (
  <TableHead>
    <HeaderRow>
      {headerCells.map(({ value, label, isSortable, width }) => (
        <HeaderCell
          id={value || label}
          key={value || label}
          padding="normal"
          align="left"
          onClick={
            isSortable
              ? () =>
                  onSortUpdate(
                    value,
                    sortParams.sortOrder === 'ASC' ? 'DESC' : 'ASC',
                  )
              : null
          }
        >
          <div className={cx({ [styles.sortable]: isSortable })}>
            <div className={styles.cellHeader} style={{ width }}>
              {label}
            </div>
            {isSortable && sortParams && (
              <SortIcon
                sortOrder={sortParams.sortOrder}
                currentlySorted={
                  sortParams.sortBy && Array.isArray(sortParams.sortBy)
                    ? sortParams.sortBy.includes(value)
                    : sortParams.sortBy === value
                }
              />
            )}
          </div>
        </HeaderCell>
      ))}
    </HeaderRow>
  </TableHead>
);

export default function TableFull(props) {
  const {
    children,
    headerCells = [],
    onSortUpdate = () => {},
    onPageChange = () => {},
    // onClickCheckbox = () => {},
    toolsBar = null,
    params = null,
    // openTools = false,
    count = 0,
    loading = false,
    customMinHeight = undefined,
    customMaxHeight = undefined,
  } = props;
  const hasData = React.Children.count(children) > 0;

  return (
    <Grid className={styles.tableContainer} container item>
      <Grid item xs={12} style={{ width: '100%' }}>
        <TablePaper min_height={customMinHeight} max_height={customMaxHeight}>
          {loading ? (
            <Grid item>
              <LinearProgress loading={loading} />
            </Grid>
          ) : null}
          {toolsBar || null}
          <div className={styles.scrollableContainer}>
            <Table fixed={false}>
              {createHeader(headerCells, onSortUpdate, params)}
              <TableBody>
                {hasData ? (
                  children
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={headerCells.length}
                      style={{ textAlign: 'center' }}
                    >
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className={styles.footer}>
            {count ? (
              <p className={styles.tableCount}>{count} Items Found</p>
            ) : null}
            {params ? (
              <Pagination
                count={count}
                rowsPerPage={params.limit}
                page={+params.page || 1}
                onChangePage={onPageChange}
                ActionsComponent={Pagination}
              />
            ) : null}
          </div>
        </TablePaper>
      </Grid>
    </Grid>
  );
}

TableFull.propTypes = {
  headerCells: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
      isSortable: PropTypes.bool,
    }),
  ),
  onSortUpdate: PropTypes.func,
  onPageChange: PropTypes.func,
  toolsBar: PropTypes.element,
  params: PropTypes.shape({
    search: PropTypes.string,
    page: PropTypes.number,
    offset: PropTypes.number,
    limit: PropTypes.number,
    sortBy: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    sortOrder: PropTypes.string,
  }),
  count: PropTypes.number,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  loading: PropTypes.bool,
  customMinHeight: PropTypes.string,
  customMaxHeight: PropTypes.string,
};
